// lib/services/geometry/meshdata_factory.ts

import MeshData from "./meshdata";
import { MeshPrimitiveData } from "./modeldata";

export function makeStaticMeshDataFromPrimitive(
  primitive: Pick<MeshPrimitiveData, "positions" | "normals" | "uvs" | "indices">
): MeshData {
  const positions = primitive.positions;
  const normals = primitive.normals;
  const uvs = primitive.uvs;

  const vertexCount = positions.length / 3;
  const vertices = new Float32Array(vertexCount * 8);

  for (let i = 0; i < vertexCount; i++) {
    const v = i * 8;
    const p = i * 3;
    const n = i * 3;
    const t = i * 2;

    vertices[v + 0] = positions[p + 0];
    vertices[v + 1] = positions[p + 1];
    vertices[v + 2] = positions[p + 2];

    vertices[v + 3] = normals ? normals[n + 0] : 0;
    vertices[v + 4] = normals ? normals[n + 1] : 1;
    vertices[v + 5] = normals ? normals[n + 2] : 0;

    vertices[v + 6] = uvs ? uvs[t + 0] : 0;
    vertices[v + 7] = uvs ? uvs[t + 1] : 0;
  }

  const indices = toEngineIndices(primitive.indices, vertexCount);

  return new MeshData(
    new Uint8Array(vertices.buffer.slice(0)),
    indices,
    32,
    false
  );
}

export function makeSkinnedMeshDataFromPrimitive(
  primitive: Pick<MeshPrimitiveData, "positions" | "normals" | "uvs" | "joints0" | "weights0" | "indices">
): MeshData {
  const positions = primitive.positions;
  const normals = primitive.normals;
  const uvs = primitive.uvs;
  const joints0 = primitive.joints0;
  const weights0 = primitive.weights0;

  const vertexCount = positions.length / 3;
  const vertexBytes = new ArrayBuffer(vertexCount * 64);
  const f32 = new Float32Array(vertexBytes);
  const u32 = new Uint32Array(vertexBytes);

  for (let i = 0; i < vertexCount; i++) {
    const base = i * 16;

    f32[base + 0] = positions[i * 3 + 0];
    f32[base + 1] = positions[i * 3 + 1];
    f32[base + 2] = positions[i * 3 + 2];

    f32[base + 3] = normals ? normals[i * 3 + 0] : 0;
    f32[base + 4] = normals ? normals[i * 3 + 1] : 1;
    f32[base + 5] = normals ? normals[i * 3 + 2] : 0;

    f32[base + 6] = uvs ? uvs[i * 2 + 0] : 0;
    f32[base + 7] = uvs ? uvs[i * 2 + 1] : 0;

    u32[base + 8]  = joints0 ? Number(joints0[i * 4 + 0]) : 0;
    u32[base + 9]  = joints0 ? Number(joints0[i * 4 + 1]) : 0;
    u32[base + 10] = joints0 ? Number(joints0[i * 4 + 2]) : 0;
    u32[base + 11] = joints0 ? Number(joints0[i * 4 + 3]) : 0;

    f32[base + 12] = weights0 ? weights0[i * 4 + 0] : 1;
    f32[base + 13] = weights0 ? weights0[i * 4 + 1] : 0;
    f32[base + 14] = weights0 ? weights0[i * 4 + 2] : 0;
    f32[base + 15] = weights0 ? weights0[i * 4 + 3] : 0;
  }

  const indices = toEngineIndices(primitive.indices, vertexCount);

  return new MeshData(
    new Uint8Array(vertexBytes),
    indices,
    64,
    true
  );
}

// WebGPU natively supports both 16-bit and 32-bit index buffers
// (GPUIndexFormat "uint16" | "uint32" - see
// https://www.w3.org/TR/webgpu/#enumdef-gpuindexformat), so this no
// longer downcasts/truncates Uint32Array sources. A Uint32Array is
// preserved as-is; a Uint32Array is only ever produced upstream (by the
// glTF loader) for UNSIGNED_INT accessors, so its presence already means
// "this primitive needs 32-bit indices" - there is nothing to validate
// against vertexCount here beyond what WebGPU itself supports natively.
// Uint16Array sources (including ones promoted from UNSIGNED_BYTE
// accessors upstream) are passed through unchanged. Missing indices
// (non-indexed primitives) synthesize a sequential Uint16Array unless the
// vertex count itself exceeds the 16-bit range, in which case a
// Uint32Array is synthesized instead so the generated indices can still
// address every vertex.
function toEngineIndices(
  source: Uint16Array | Uint32Array | undefined,
  vertexCount: number
): Uint16Array | Uint32Array {
  if (source) {
    return source;
  }

  if (vertexCount > 65535) {
    const indices = new Uint32Array(vertexCount);
    for (let i = 0; i < vertexCount; i++) {
      indices[i] = i;
    }
    return indices;
  }

  const indices = new Uint16Array(vertexCount);
  for (let i = 0; i < vertexCount; i++) {
    indices[i] = i;
  }
  return indices;
}
